import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { actionRequiredService, GET_FOLLOW_UP_QUESTIONS } from '@/api';
import { Button, LoaderScreen } from '@/components/common';
import { SurveyQuestionsModal } from '@/components/modals/survey/SurveyQuestionsModal';
import { CaseQuestionI, SendFollowUpPayload } from '@/interfaces';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';

interface Props {
	id: number;
	hasNextDocument: boolean;
	savedAnswers?: SendFollowUpPayload;
	onSaveAnswers: (id: number, payload: SendFollowUpPayload) => void;
	onContinue: (isApproved: boolean) => void;
}

export const FollowUp: React.FC<Props> = ({
	id,
	savedAnswers,
	onSaveAnswers,
	onContinue,
}) => {
	const { toastError } = useNotification();
	const isViewOnly = !!savedAnswers;

	const { data, isLoading } = useQuery({
		queryKey: [GET_FOLLOW_UP_QUESTIONS, id],
		queryFn: () => actionRequiredService.getFollowUp(id),
		enabled: !!id,
	});

	const { mutateAsync: sendFollowUp, isPending } = useMutation({
		mutationFn: (payload: SendFollowUpPayload) =>
			actionRequiredService.sendFollowUp(id, payload),
		onError: () => toastError(formatErrorMessage(undefined)),
	});

	const handleDecline = async () => {
		const payload: SendFollowUpPayload = { answers: [], decline: true };
		await sendFollowUp(payload);
		onSaveAnswers(id, payload);
		onContinue(false);
	};

	const questions: CaseQuestionI[] = React.useMemo(() => {
		if (!data?.data?.questions) return [];
		return data.data.questions.map((q) => {
			const savedAnswer = savedAnswers?.answers.find(
				(a) => a.question_id === q.id
			);

			return {
				id: q.id,
				question: q.text,
				question_type: q.type,
				multiple_choise_questions: (q.options ?? []).map((option, index) => ({
					id: index,
					brand: option.text,
					complement: '',
					answer: savedAnswer?.answers?.includes(option.text),
				})),
				ask_explanation: q.explanation,
				incorrect_answer: q.options
					?.find((o) => !o.is_correct)
					?.text?.toUpperCase(),
				answer: savedAnswer?.answer,
				explanation_answer: savedAnswer?.detail,
			};
		});
	}, [data, savedAnswers]);

	if (isLoading) return <LoaderScreen />;

	return (
		<div className="flex flex-col flex-1 min-h-0 h-full">
			<SurveyQuestionsModal
				title=""
				questions={questions}
				isOpen={true}
				isViewOnly={isViewOnly}
				heading={data?.data?.heading}
				description={data?.data?.description}
				withoutModal
				onSubmit={async (payload) => {
					await sendFollowUp({ ...payload, decline: false });
					onSaveAnswers(id, { ...payload, decline: false });
					onContinue(true);
				}}
				renderFooter={(handleSubmit, isSubmitting) =>
					isViewOnly ? (
						<div className="flex w-full justify-end gap-2 bg-white pt-4 pb-2">
							<Button
								label="Continue"
								size="fit"
								decoration="primary"
								onClick={() => onContinue(true)}
							/>
						</div>
					) : (
						<div className="flex w-full justify-between gap-2 bg-white pt-4 pb-2">
							<Button
								label="Decline"
								size="fit"
								decoration="danger-outline"
								onClick={handleDecline}
								disabled={isSubmitting || isPending}
								loading={isPending}
							/>
							<Button
								label="Complete"
								size="fit"
								decoration="primary"
								loading={isSubmitting}
								disabled={isPending}
								onClick={handleSubmit}
							/>
						</div>
					)
				}
			/>
		</div>
	);
};
